import Glide from "@glidejs/glide";

// Store Vimeo players by iframe ID
const vimeoPlayerMap = {};

const initialiseSliders = () => {
    const sliders = document.querySelectorAll(".home-page-banner__glide");
    if (sliders.length === 0) return;

    sliders.forEach((sliderElement) => {
        if (!sliderElement.hasAttribute("data-initialized")) {
            const autoplayAttr = sliderElement.getAttribute("data-autoplay");
            const autoplayValue =
                autoplayAttr && autoplayAttr.toLowerCase() === "true" ? 3000 : false;

            const glide = new Glide(sliderElement, {
                type: "carousel",
                startAt: 0,
                perView: 1,
                autoplay: 3000,
                gap: 0,
                hoverpause: true,
            }).mount();

            const currentSlide = sliderElement.querySelector(
                ".home-page-banner__current-slide",
            );
            const totalSlides = sliderElement.querySelector(
                ".home-page-banner__total-slides",
            );
            const toggleButton = sliderElement.querySelector(".glide__toggle");

            const updateSlideInfo = () => {
                if (currentSlide && totalSlides) {
                    currentSlide.textContent = glide.index + 1;
                    totalSlides.textContent = glide._c.Html.slides.length;
                }
            };

            updateSlideInfo();
            glide.on(["mount.after", "run"], updateSlideInfo);

            if (toggleButton) {
                toggleButton.addEventListener("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    if (toggleButton.classList.contains("pause")) {
                        glide.pause();
                        toggleButton.classList.remove("pause");
                        toggleButton.classList.add("play");
                    } else {
                        glide.play();
                        toggleButton.classList.remove("play");
                        toggleButton.classList.add("pause");
                    }
                });
            }

            sliderElement.setAttribute("data-initialized", "true");
        }
    });
};

// Video Poster Z-Index Manager with Play/Pause Controls
const initializeVideoPosterZIndex = () => {
    // Vimeo API loading function
    const ensureVimeoAPIReady = () => {
        return new Promise((resolve, reject) => {
            if (window.Vimeo && window.Vimeo.Player) {
                return resolve(window.Vimeo);
            }

            if (document.querySelector('script[src="https://player.vimeo.com/api/player.js"]')) {
                const checkVimeo = setInterval(() => {
                    if (window.Vimeo && window.Vimeo.Player) {
                        clearInterval(checkVimeo);
                        resolve(window.Vimeo);
                    }
                }, 100);
                return;
            }

            const tag = document.createElement("script");
            tag.src = "https://player.vimeo.com/api/player.js";
            tag.onload = () => resolve(window.Vimeo);
            tag.onerror = reject;

            const firstScriptTag = document.getElementsByTagName("script")[0];
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        });
    };

    // Update poster z-index based on video toggle button state
    const updatePosterZIndex = (section) => {
        if (!section) return;
        
        const videoControls = section.querySelector('.home-page-banner__video-controls');
        if (!videoControls) return;
        
        const toggleButton = videoControls.querySelector('.video-toggle');
        if (!toggleButton) return;
        
        const isUserInitiated = section.dataset.userInitiated === 'true';
        const videoWrappers = section.querySelectorAll('.home-page-banner__background-video-wrapper');
        
        console.log('[Video Poster] updatePosterZIndex called', {
            isUserInitiated,
            buttonState: toggleButton.classList.contains('pause') ? 'pause' : 'play',
            userInitiatedFlag: section.dataset.userInitiated
        });
        
        videoWrappers.forEach(wrapper => {
            const poster = wrapper.querySelector('.home-page-banner__poster-image');
            if (poster) {
                if (toggleButton.classList.contains('pause')) {
                    poster.style.setProperty('z-index', '0', 'important');
                    console.log('[Video Poster] Setting poster z-index to 0 (video playing)');
                } else if (toggleButton.classList.contains('play')) {
                    if (!isUserInitiated) {
                        poster.style.setProperty('z-index', '1', 'important');
                        console.log('[Video Poster] Setting poster z-index to 1 (autoplay/not user-initiated)');
                    } else {
                        poster.style.setProperty('z-index', '0', 'important');
                        console.log('[Video Poster] Setting poster z-index to 0 (user-initiated play)');
                    }
                }
            }
        });
    };

    // Helper function to update button state when video play/pause events occur
    const updateVideoControlButton = (wrapper, isPlaying) => {
        const section = wrapper.closest('.wp-block-block-builder-federalgroup-home-page-banner');
        if (!section) return;
        
        const videoControls = section.querySelector('.home-page-banner__video-controls');
        if (!videoControls) return;
        
        const toggleButton = videoControls.querySelector('.video-toggle');
        if (!toggleButton) return;
        
        const isUserInitiated = section.dataset.userInitiated === 'true';
        console.log('[Video Control Button] Updating button state', {
            isPlaying,
            isUserInitiated,
            source: isUserInitiated ? 'USER_INTERACTION' : 'AUTOPLAY'
        });
        
        if (isPlaying) {
            toggleButton.classList.add("pause");
            toggleButton.classList.remove("play");
        } else {
            toggleButton.classList.add("play");
            toggleButton.classList.remove("pause");
        }
        
        updatePosterZIndex(section);
    };

    // Setup video controls with poster z-index management
    const setupVideoControls = () => {
        const videoControls = document.querySelectorAll(".home-page-banner__video-controls");
        if (!videoControls.length) return;

        videoControls.forEach((controlElement) => {
            const toggleButton = controlElement.querySelector(".video-toggle");
            if (!toggleButton) return;

            const videoType = controlElement.getAttribute('data-video-type');
            const section = controlElement.closest('.wp-block-block-builder-federalgroup-home-page-banner');
            
            if (videoType === "video-youtube") {
                controlElement.style.display = "none";
                return;
            }
            
            let videoElement;
            if (videoType === "video-local") {
                videoElement = section.querySelector('video#local-video') || section.querySelector('video#local-video-mobile');
            } else if (videoType === "video-vimeo") {
                videoElement = section.querySelector('iframe#vimeo-iframe') || section.querySelector('iframe#vimeo-iframe-mobile');
            }
            
            if (!videoElement) return;
            
            // Check actual video state instead of assuming paused
            const checkVideoState = (buttonToUpdate) => {
                // Find the visible video element
                let visibleVideo = null;
                if (videoType === "video-local") {
                    const desktopVideo = section.querySelector('video#local-video');
                    const mobileVideo = section.querySelector('video#local-video-mobile');
                    const desktopWrapper = desktopVideo?.closest('.home-page-banner__background-video-wrapper');
                    const mobileWrapper = mobileVideo?.closest('.home-page-banner__background-video-wrapper');
                    
                    const desktopVisible = desktopWrapper && window.getComputedStyle(desktopWrapper).display !== 'none';
                    const mobileVisible = mobileWrapper && window.getComputedStyle(mobileWrapper).display !== 'none';
                    
                    if (desktopVisible && desktopVideo) {
                        visibleVideo = desktopVideo;
                    } else if (mobileVisible && mobileVideo) {
                        visibleVideo = mobileVideo;
                    }
                    
                    if (visibleVideo) {
                        // Check if video is actually playing
                        const isPlaying = !visibleVideo.paused && !visibleVideo.ended && visibleVideo.readyState > 2;
                        if (isPlaying) {
                            buttonToUpdate.classList.add("pause");
                            buttonToUpdate.classList.remove("play");
                        } else {
                            buttonToUpdate.classList.add("play");
                            buttonToUpdate.classList.remove("pause");
                        }
                        updatePosterZIndex(section);
                    }
                } else if (videoType === "video-vimeo") {
                    ensureVimeoAPIReady().then(Vimeo => {
                        const desktopIframe = section.querySelector('iframe#vimeo-iframe');
                        const mobileIframe = section.querySelector('iframe#vimeo-iframe-mobile');
                        const desktopWrapper = desktopIframe?.closest('.home-page-banner__background-video-wrapper');
                        const mobileWrapper = mobileIframe?.closest('.home-page-banner__background-video-wrapper');
                        
                        const desktopVisible = desktopWrapper && window.getComputedStyle(desktopWrapper).display !== 'none';
                        const mobileVisible = mobileWrapper && window.getComputedStyle(mobileWrapper).display !== 'none';
                        
                        let visibleIframe = null;
                        if (desktopVisible && desktopIframe) {
                            visibleIframe = desktopIframe;
                        } else if (mobileVisible && mobileIframe) {
                            visibleIframe = mobileIframe;
                        }
                        
                        if (visibleIframe) {
                            try {
                                const player = new Vimeo.Player(visibleIframe);
                                player.getPaused().then(paused => {
                                    if (!paused) {
                                        buttonToUpdate.classList.add("pause");
                                        buttonToUpdate.classList.remove("play");
                                    } else {
                                        buttonToUpdate.classList.add("play");
                                        buttonToUpdate.classList.remove("pause");
                                    }
                                    updatePosterZIndex(section);
                                }).catch(() => {
                                    // Fallback to paused state if API call fails
                                    buttonToUpdate.classList.add("play");
                                    buttonToUpdate.classList.remove("pause");
                                    updatePosterZIndex(section);
                                });
                            } catch (error) {
                                // Fallback to paused state if player creation fails
                                buttonToUpdate.classList.add("play");
                                buttonToUpdate.classList.remove("pause");
                                updatePosterZIndex(section);
                            }
                        }
                    }).catch(() => {
                        // Fallback to paused state if Vimeo API fails to load
                        buttonToUpdate.classList.add("play");
                        buttonToUpdate.classList.remove("pause");
                        updatePosterZIndex(section);
                    });
                }
            };
            
            // Check immediately and with delayed checks to catch autoplay
            checkVideoState(toggleButton);
            
            const newToggleButton = toggleButton.cloneNode(true);
            toggleButton.parentNode.replaceChild(newToggleButton, toggleButton);
            
            // Check state again after cloning with delays to catch autoplay that happens after initialization
            setTimeout(() => checkVideoState(newToggleButton), 100);
            setTimeout(() => checkVideoState(newToggleButton), 500);

            let vimeoPlayers = [];
            const handleVimeoToggle = () => {
                ensureVimeoAPIReady().then(Vimeo => {
                    if (vimeoPlayers.length === 0) {
                        const allVimeoIframes = section.querySelectorAll('iframe[id*="vimeo-iframe"]');
                        allVimeoIframes.forEach(iframe => {
                            vimeoPlayers.push(new Vimeo.Player(iframe));
                        });
                    }
                    
                    if (newToggleButton.classList.contains("pause")) {
                        Promise.all(vimeoPlayers.map(player => player.pause())).then(() => {
                            newToggleButton.classList.remove("pause");
                            newToggleButton.classList.add("play");
                            updatePosterZIndex(section);
                        }).catch(() => {});
                    } else {
                        Promise.all(vimeoPlayers.map(player => player.play())).then(() => {
                            newToggleButton.classList.remove("play");
                            newToggleButton.classList.add("pause");
                            updatePosterZIndex(section);
                        }).catch(() => {});
                    }
                });
            };

            newToggleButton.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                console.log('[Video Controls] User clicked video toggle button', {
                    videoType,
                    currentState: newToggleButton.classList.contains('pause') ? 'pause' : 'play'
                });
                
                section.dataset.userInitiated = 'true';
                console.log('[Video Controls] Set userInitiated flag to true');
                
                if (videoType === "video-local") {
                    const allLocalVideos = section.querySelectorAll('video.home-page-banner__background-video');
                    
                    if (newToggleButton.classList.contains("pause")) {
                        console.log('[Video Controls] User pausing local video');
                        allLocalVideos.forEach(video => video.pause());
                        newToggleButton.classList.remove("pause");
                        newToggleButton.classList.add("play");
                    } else {
                        console.log('[Video Controls] User playing local video');
                        allLocalVideos.forEach(video => {
                            const playPromise = video.play();
                            if (playPromise !== undefined) {
                                playPromise.catch(() => {});
                            }
                        });
                        newToggleButton.classList.remove("play");
                        newToggleButton.classList.add("pause");
                    }
                } else if (videoType === "video-vimeo") {
                    console.log('[Video Controls] User toggling Vimeo video');
                    handleVimeoToggle();
                }
                
                updatePosterZIndex(section);
                
                setTimeout(() => {
                    delete section.dataset.userInitiated;
                    console.log('[Video Controls] Cleared userInitiated flag after 1 second');
                }, 1000);
            });
        });
    };

    // Setup video event listeners
    const handleIframeVideos = async () => {
        const videoWrappers = document.querySelectorAll(".home-page-banner__background-video-wrapper");
        if (!videoWrappers.length) return;

        for (const wrapper of videoWrappers) {
            const videoType = wrapper.dataset.videoType;

            if (videoType === "video-local") {
                const video = wrapper.querySelector('video');
                if (video) {
                    video.addEventListener('playing', () => {
                        const isVisible = window.getComputedStyle(wrapper).display !== 'none';
                        const isUserInitiated = wrapper.closest('.wp-block-block-builder-federalgroup-home-page-banner')?.dataset.userInitiated === 'true';
                        console.log('[Local Video] Video started playing', {
                            isVisible,
                            isUserInitiated,
                            source: isUserInitiated ? 'USER_INTERACTION' : 'AUTOPLAY'
                        });
                        if (isVisible) {
                            updateVideoControlButton(wrapper, true);
                        }
                    });

                    video.addEventListener('pause', () => {
                        const isVisible = window.getComputedStyle(wrapper).display !== 'none';
                        console.log('[Local Video] Video paused', { isVisible });
                        if (isVisible) {
                            updateVideoControlButton(wrapper, false);
                        }
                    });

                    if (video.videoHeight >= 1080) {
                        video.style.minHeight = '1080px';
                    }
                }
            }

            if (videoType === "video-vimeo") {
                try {
                    const Vimeo = await ensureVimeoAPIReady();
                    const iframe = wrapper.querySelector('iframe');
                    
                    if (iframe) {
                        const player = new Vimeo.Player(iframe);
                        
                        player.ready().then(() => {
                            player.setQuality('1080p')
                                .catch(() => player.setQuality('720p'))
                                .catch(() => {});
                        });

                        player.on('play', () => {
                            const isVisible = window.getComputedStyle(wrapper).display !== 'none';
                            const section = wrapper.closest('.wp-block-block-builder-federalgroup-home-page-banner');
                            const isUserInitiated = section?.dataset.userInitiated === 'true';
                            console.log('[Vimeo Video] Video started playing', {
                                isVisible,
                                isUserInitiated,
                                source: isUserInitiated ? 'USER_INTERACTION' : 'AUTOPLAY'
                            });
                            if (isVisible) {
                                updateVideoControlButton(wrapper, true);
                            }
                        });
                        
                        player.on('pause', () => {
                            const isVisible = window.getComputedStyle(wrapper).display !== 'none';
                            console.log('[Vimeo Video] Video paused', { isVisible });
                            if (isVisible) {
                                updateVideoControlButton(wrapper, false);
                            }
                        });

                        player.on('loaded', () => {
                            player.setLoop(true);
                        });
                    }
                } catch (error) {
                    // Error initializing Vimeo player
                }
            }
        }
    };

    // Initialize
    setupVideoControls();
    handleIframeVideos();
};

// Handle responsive video switching
const handleResponsiveVideos = () => {
    const videoWrappers = document.querySelectorAll('.home-page-banner__background-video-wrapper');
    
    const resetPosterImages = () => {
        videoWrappers.forEach(wrapper => {
            const isVisible = window.getComputedStyle(wrapper).display !== 'none';
            const poster = wrapper.querySelector('.home-page-banner__poster-image');
            
            if (isVisible && poster) {
                // CRITICAL: Always reset to show poster on resize unless video was already confirmed playing
                const wasPlaying = wrapper.classList.contains('playing');
                
                if (!wasPlaying) {
                    wrapper.classList.remove('transitioning');
                    poster.style.opacity = '1';
                    poster.style.visibility = 'visible';
                }
                
                const iframe = wrapper.querySelector('iframe');
                
                if (iframe && !wasPlaying) {
                    setTimeout(() => {
                        const src = iframe.src;
                        if (src && !src.includes('autoplay=1')) {
                            iframe.src = src + (src.includes('?') ? '&' : '?') + 'autoplay=1';
                        }
                    }, 100);
                }
            }
        });
    };
    
    let resizeTimeout;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(resetPosterImages, 100);
    });
    
    videoWrappers.forEach(wrapper => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const video = entry.target.querySelector('video');
                    
                    if (video && video.readyState === 0) {
                        video.load();
                    }
                }
            });
        });
        
        observer.observe(wrapper);
    });
};

// Initialize everything when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        initialiseSliders();
        initializeVideoPosterZIndex();
        handleResponsiveVideos();
    });
} else {
    initialiseSliders();
    initializeVideoPosterZIndex();
    handleResponsiveVideos();
}
